import { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styles from "./ExportDropdown.module.css";

export type ExportFormat = "markdown" | "wordpress" | "docx" | "gdoc";

/** Human-readable display names for toast messages */
export const FORMAT_DISPLAY_NAMES: Record<ExportFormat, string> = {
  markdown: "Markdown",
  wordpress: "WordPress HTML",
  docx: "Word Document",
  gdoc: "Google Docs–compatible document",
};

export interface ExportDropdownProps {
  orgId: string;
  documentId: string;
  documentTitle: string;
  onExportStart?: () => void;
  onExportComplete?: (format: ExportFormat) => void;
  onExportError?: (error: string) => void;
}

const FORMAT_CONFIG: Record<
  ExportFormat,
  { label: string; icon: React.ReactNode; extension: string }
> = {
  markdown: {
    label: "Markdown",
    icon: <MarkdownIcon />,
    extension: ".md",
  },
  wordpress: {
    label: "WordPress (HTML)",
    icon: <WordPressIcon />,
    extension: ".html",
  },
  docx: {
    label: "Word Document",
    icon: <WordIcon />,
    extension: ".docx",
  },
  gdoc: {
    label: "Google Docs",
    icon: <GoogleDocsIcon />,
    extension: ".docx",
  },
};

export function ExportDropdown({
  orgId,
  documentId,
  documentTitle,
  onExportStart,
  onExportComplete,
  onExportError,
}: ExportDropdownProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [exporting, setExporting] = useState<ExportFormat | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleExport = useCallback(
    async (format: ExportFormat) => {
      setExporting(format);
      onExportStart?.();

      try {
        const response = await fetch(
          `/api/organizations/${orgId}/documents/${documentId}/export/${format}`,
          {
            credentials: "include",
          },
        );

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || `Export failed: ${response.status}`);
        }

        // Get the blob and download it
        const blob = await response.blob();
        const filename = `${sanitizeFilename(documentTitle)}${FORMAT_CONFIG[format].extension}`;

        // Create download link
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        onExportComplete?.(format);
        setIsOpen(false);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Export failed";
        onExportError?.(message);
      } finally {
        setExporting(null);
      }
    },
    [orgId, documentId, documentTitle, onExportStart, onExportComplete, onExportError],
  );

  const handleCopyMarkdown = useCallback(async () => {
    setExporting("markdown");
    onExportStart?.();

    try {
      const response = await fetch(
        `/api/organizations/${orgId}/documents/${documentId}/export/markdown`,
        {
          credentials: "include",
        },
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Export failed: ${response.status}`);
      }

      const text = await response.text();
      await navigator.clipboard.writeText(text);

      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
      onExportComplete?.("markdown");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Copy failed";
      onExportError?.(message);
    } finally {
      setExporting(null);
    }
  }, [orgId, documentId, onExportStart, onExportComplete, onExportError]);

  return (
    <div className={styles.container} ref={dropdownRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <ExportIcon />
        {t("editor.export")}
        <ChevronIcon expanded={isOpen} />
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{t("editor.downloadAs")}</div>
            {(Object.keys(FORMAT_CONFIG) as ExportFormat[]).map((format) => (
              <button
                key={format}
                type="button"
                className={styles.option}
                onClick={() => handleExport(format)}
                disabled={exporting !== null}
              >
                {FORMAT_CONFIG[format].icon}
                <span className={styles.optionLabel}>{FORMAT_CONFIG[format].label}</span>
                {exporting === format && <span className={styles.spinner} />}
              </button>
            ))}
          </div>

          <div className={styles.divider} />

          <div className={styles.section}>
            <button
              type="button"
              className={`${styles.option} ${copySuccess ? styles.optionSuccess : ""}`}
              onClick={handleCopyMarkdown}
              disabled={exporting !== null}
            >
              {copySuccess ? <CheckIcon /> : <CopyIcon />}
              <span className={styles.optionLabel}>
                {copySuccess ? "Copied!" : "Copy as Markdown"}
              </span>
              {exporting === "markdown" && !copySuccess && <span className={styles.spinner} />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function sanitizeFilename(name: string): string {
  return name
    .replace(/[^a-zA-Z0-9\s-_]/g, "")
    .replace(/\s+/g, "-")
    .toLowerCase()
    .slice(0, 50);
}

function ExportIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M14 10V12.667A1.333 1.333 0 0112.667 14H3.333A1.333 1.333 0 012 12.667V10M4.667 6.667L8 10M8 10L11.333 6.667M8 10V2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      style={{
        transform: expanded ? "rotate(180deg)" : undefined,
        transition: "transform 0.15s ease",
      }}
    >
      <path
        d="M3 4.5L6 7.5L9 4.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MarkdownIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="1" y="3" width="14" height="10" rx="1" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M4 11V6L6 8.5L8 6V11"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 8.5L10.5 10L12 11.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function WordPressIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M3 8L5.5 13M8 3L10.5 13M13 8L10.5 13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function WordIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="2" width="12" height="12" rx="1" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M5 5L7 11L8 8.5L9 11L11 5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function GoogleDocsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M4 2H10L13 5V13C13 13.5523 12.5523 14 12 14H4C3.44772 14 3 13.5523 3 13V3C3 2.44772 3.44772 2 4 2Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 2V5H13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M6 8H10M6 10.5H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="5" y="5" width="8" height="8" rx="1" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M3 11V3H11"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M13.333 4L6 11.333L2.667 8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
